/**
 * Aba Identidade Visual — logo + cor primária.
 *
 * Logo: PNG/JPG/SVG/WebP até 2MB. Upload via multipart, preview imediato.
 * Cor primária: #RRGGBB (color picker + texto).
 */

import { useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Image as ImageIcon, Trash2, Upload } from "lucide-react";
import toast from "react-hot-toast";

import { tenantsCompanyApi } from "@/api/tenantsCompany";
import { apiErrorMessage } from "@/api/client";
import { qk, useTenantCompany } from "@/hooks/queries";

import { Button } from "@/components/Button";
import { Card, CardBody, CardHeader } from "@/components/Card";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Field, Input } from "@/components/Field";
import { Skeleton } from "@/components/Skeleton";

const MAX_BYTES = 2 * 1024 * 1024;
const ACCEPTED = "image/png,image/jpeg,image/svg+xml,image/webp";

export function IdentityTab({ tenantId }: { tenantId: string }) {
  const company = useTenantCompany(tenantId);
  const qc = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);

  const [corPrimaria, setCorPrimaria] = useState<string>("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [uploadVersion, setUploadVersion] = useState(0);   // bust cache do logo após upload

  // Inicializa cor quando company chega
  useEffect(() => {
    setCorPrimaria(company.data?.cor_primaria || "");
  }, [company.data?.cor_primaria]);

  const uploadMut = useMutation({
    mutationFn: (file: File) => tenantsCompanyApi.uploadLogo(tenantId, file),
    onSuccess: () => {
      toast.success("Logo enviado");
      setUploadVersion((v) => v + 1);
      void qc.invalidateQueries({ queryKey: qk.tenantCompany(tenantId) });
      void qc.invalidateQueries({ queryKey: qk.tenantAuditLogs(tenantId) });
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const deleteMut = useMutation({
    mutationFn: () => tenantsCompanyApi.deleteLogo(tenantId),
    onSuccess: () => {
      toast.success("Logo removido");
      setConfirmDelete(false);
      setUploadVersion((v) => v + 1);
      void qc.invalidateQueries({ queryKey: qk.tenantCompany(tenantId) });
      void qc.invalidateQueries({ queryKey: qk.tenantAuditLogs(tenantId) });
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const corMut = useMutation({
    mutationFn: (cor: string | null) =>
      tenantsCompanyApi.updateCompany(tenantId, { cor_primaria: cor }),
    onSuccess: () => {
      toast.success("Cor primária atualizada");
      void qc.invalidateQueries({ queryKey: qk.tenantCompany(tenantId) });
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  if (company.isLoading) return <Skeleton className="h-72 w-full" />;
  if (!company.data) return null;

  const c = company.data;
  const hasLogo = !!c.logo_path;
  const logoUrl = `${tenantsCompanyApi.logoUrl(tenantId).split("?")[0]}?v=${uploadVersion}`;

  function onFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_BYTES) {
      toast.error(`Arquivo muito grande. Máximo ${MAX_BYTES / 1024 / 1024} MB.`);
      e.target.value = "";
      return;
    }
    uploadMut.mutate(file);
    e.target.value = "";
  }

  function onCorBlur() {
    const cleaned = corPrimaria.trim();
    if (cleaned === (c.cor_primaria || "")) return;
    if (cleaned && !/^#[0-9A-Fa-f]{6}$/.test(cleaned)) {
      toast.error("Cor inválida — use formato #RRGGBB");
      return;
    }
    corMut.mutate(cleaned || null);
  }

  return (
    <div className="space-y-4">
      {/* ---------- Logo ---------- */}
      <Card>
        <CardHeader
          title={
            <span className="inline-flex items-center gap-2">
              <ImageIcon className="h-4 w-4 text-brand-600" /> Logo
            </span>
          }
          subtitle="PNG, JPG, SVG ou WebP — máx 2 MB. Aparece nos PDFs de relatório."
        />
        <CardBody>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
            {/* Preview */}
            <div className="flex h-40 w-40 flex-none items-center justify-center rounded-lg border-2 border-dashed border-surface-300 dark:border-slate-700 bg-surface-50 dark:bg-slate-800/40">
              {hasLogo ? (
                <img
                  src={logoUrl}
                  alt="Logo do tenant"
                  className="max-h-full max-w-full rounded object-contain"
                />
              ) : (
                <div className="flex flex-col items-center gap-1 text-slate-400 dark:text-slate-500">
                  <ImageIcon className="h-8 w-8" />
                  <span className="text-xs">Sem logo</span>
                </div>
              )}
            </div>

            {/* Ações */}
            <div className="flex-1 space-y-3">
              <input
                ref={fileInput}
                type="file"
                accept={ACCEPTED}
                onChange={onFileChange}
                className="hidden"
              />
              <div className="flex flex-wrap gap-2">
                <Button
                  icon={<Upload className="h-4 w-4" />}
                  onClick={() => fileInput.current?.click()}
                  loading={uploadMut.isPending}
                >
                  {hasLogo ? "Trocar logo" : "Enviar logo"}
                </Button>
                {hasLogo && (
                  <Button
                    variant="secondary"
                    icon={<Trash2 className="h-4 w-4 text-red-500" />}
                    onClick={() => setConfirmDelete(true)}
                  >
                    Remover
                  </Button>
                )}
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Recomendado: imagem com fundo transparente (PNG ou SVG), proporção
                horizontal ou quadrada. Max 2 MB.
              </p>
              {hasLogo && (
                <p className="text-xs text-slate-400 dark:text-slate-500">
                  Arquivo atual: <code>{c.logo_path}</code>
                </p>
              )}
            </div>
          </div>
        </CardBody>
      </Card>

      {/* ---------- Cor primária ---------- */}
      <Card>
        <CardHeader
          title="Cor primária"
          subtitle="Cor de destaque usada nos relatórios PDF (header, separadores)"
        />
        <CardBody>
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={corPrimaria || "#22d3ee"}
                onChange={(e) => setCorPrimaria(e.target.value)}
                onBlur={onCorBlur}
                className="h-10 w-16 cursor-pointer rounded border border-surface-300 dark:border-slate-700"
              />
              <Field label="HEX">
                <Input
                  value={corPrimaria}
                  onChange={(e) => setCorPrimaria(e.target.value)}
                  onBlur={onCorBlur}
                  placeholder="#22d3ee"
                  pattern="^#[0-9A-Fa-f]{6}$"
                  maxLength={7}
                  className="w-32 font-mono"
                />
              </Field>
            </div>
            {corPrimaria && /^#[0-9A-Fa-f]{6}$/.test(corPrimaria) && (
              <div
                className="flex items-center gap-3 rounded-lg border border-surface-200 dark:border-slate-800 px-3 py-2"
                style={{ borderLeftWidth: 6, borderLeftColor: corPrimaria }}
              >
                <span
                  className="inline-block h-4 w-4 rounded"
                  style={{ backgroundColor: corPrimaria }}
                />
                <span className="text-sm text-slate-700 dark:text-slate-300">Preview da cor</span>
              </div>
            )}
          </div>
          <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">
            Salva automaticamente ao sair do campo. Default: ciano da marca envpulse.
          </p>
        </CardBody>
      </Card>

      <ConfirmDialog
        open={confirmDelete}
        title="Remover logo?"
        message="O arquivo será apagado do storage e os PDFs voltam a usar o logo padrão do sistema. Você pode enviar um novo a qualquer momento."
        confirmLabel="Remover"
        loading={deleteMut.isPending}
        onCancel={() => setConfirmDelete(false)}
        onConfirm={() => deleteMut.mutate()}
      />
    </div>
  );
}
